import React, { useState } from "react";
import { Facebook, Youtube, MessageCircle, Sparkles, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import CareerQuiz from "./CareerQuiz";

/**
 * 4-window interactive portal — the "control panel" on the landing page.
 * Each window is a compact teaser; clicking opens the full experience.
 */
export default function PortalGrid({ lang, onStartBot }) {
    const isAr = lang === "ar";
    const [quizOpen, setQuizOpen] = useState(false);
    const arrow = isAr ? <ChevronLeft size={14} /> : <ChevronRight size={14} />;

    // A featured Chaser video (public YouTube would ideally replace this).
    // If the college has an official YT channel, swap `EMBED_URL` accordingly.
    const YT_EMBED = "https://www.youtube.com/embed/videoseries?list=PLR1Kf8Zq2yrM3-lJQZuF0dJd6Bd4tG1eK&modestbranding=1&rel=0";

    return (
        <>
            <section className="mb-16" data-testid="portal-grid">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4761F]/10 border border-[#D4761F]/30 text-[#B36318] text-[11px] font-bold uppercase tracking-[0.2em] mb-3">
                        <Sparkles size={12} />
                        {isAr ? "لوحة أدوات الطالب" : "לוח הבקרה של הסטודנט"}
                    </div>
                    <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0B1F3D]">
                        {isAr ? "كل ما تحتاجه — في مكان واحد" : "כל מה שאתה צריך — במקום אחד"}
                    </h2>
                    <p className="text-[#475569] mt-2 max-w-xl mx-auto text-sm">
                        {isAr
                            ? "أخبارنا، فيديوهات تعليمية، مستشار ذكي، واختبار تحديد المسار — اختر ما يناسبك."
                            : "החדשות שלנו, סרטוני לימוד, יועץ AI, ומבחן התאמה — בחר את מה שמתאים לך."}
                    </p>
                </div>

                {/* 2x2 grid — stacks on mobile */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* ── Window 1: Facebook ── */}
                    <article
                        data-testid="portal-window-facebook"
                        className="group relative overflow-hidden rounded-2xl border border-[#1877F2]/25 bg-white shadow-[0_10px_30px_rgba(24,119,242,0.10)] hover:shadow-[0_18px_40px_rgba(24,119,242,0.25)] hover:-translate-y-1 transition-all duration-300 min-h-[280px] flex flex-col"
                    >
                        <div className="relative h-32 overflow-hidden bg-gradient-to-br from-[#1877F2] to-[#0864D3]">
                            <div
                                className="absolute inset-0 opacity-30"
                                style={{
                                    backgroundImage: "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.35) 0%, transparent 55%)",
                                }}
                            />
                            <Facebook size={80} className="absolute -end-4 -bottom-4 text-white/25" />
                            <div className="relative p-5 h-full flex flex-col justify-between">
                                <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-full self-start">
                                    {isAr ? "مباشر" : "חי עכשיו"}
                                </span>
                                <h3 className="font-serif text-xl font-bold text-white">
                                    {isAr ? "صفحتنا على فيسبوك" : "הפייסבוק שלנו"}
                                </h3>
                            </div>
                        </div>
                        <div className="p-5 flex-1 flex flex-col">
                            <p className="text-[#475569] text-sm leading-relaxed flex-1">
                                {isAr
                                    ? "منشورات يومية، فعاليات، قصص نجاح، وإعلانات فتح التسجيل."
                                    : "פוסטים יומיים, אירועים, סיפורי הצלחה, והודעות פתיחת רישום."}
                            </p>
                            <a
                                href="https://www.facebook.com/441525065717901"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="portal-fb-cta"
                                className="mt-4 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#1877F2] to-[#0864D3] hover:from-[#0864D3] hover:to-[#1877F2] text-white px-4 py-2.5 rounded-lg font-bold text-sm shadow-[0_6px_20px_rgba(24,119,242,0.35)] transition-all group-hover:scale-[1.02]"
                            >
                                {isAr ? "افتح الصفحة" : "פתח את הדף"}
                                <ExternalLink size={14} />
                            </a>
                        </div>
                    </article>

                    {/* ── Window 2: YouTube / Chaser Videos ── */}
                    <article
                        data-testid="portal-window-youtube"
                        className="group relative overflow-hidden rounded-2xl border border-[#DC2626]/25 bg-white shadow-[0_10px_30px_rgba(220,38,38,0.10)] hover:shadow-[0_18px_40px_rgba(220,38,38,0.25)] hover:-translate-y-1 transition-all duration-300 min-h-[280px] flex flex-col"
                    >
                        <div className="relative h-32 overflow-hidden bg-gradient-to-br from-[#DC2626] to-[#991B1B]">
                            <Youtube size={80} className="absolute -end-4 -bottom-4 text-white/25" />
                            <div className="relative p-5 h-full flex flex-col justify-between">
                                <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-full self-start">
                                    {isAr ? "أكثر من 721 فيديو" : "מעל 721 סרטונים"}
                                </span>
                                <h3 className="font-serif text-xl font-bold text-white">
                                    {isAr ? "فيديوهات تعليمية" : "סרטוני לימוד"}
                                </h3>
                            </div>
                        </div>
                        <div className="p-5 flex-1 flex flex-col">
                            <p className="text-[#475569] text-sm leading-relaxed flex-1">
                                {isAr
                                    ? "مكتبة الفيديوهات التعليمية على منصة تشيسر — شروحات، محاكاة امتحانات، ونصائح دراسية."
                                    : "ספריית סרטוני הלימוד של הצ׳ייסר — הסברים, סימולציות של מבחנים, וטיפים ללמידה."}
                            </p>
                            <a
                                href="https://mydoctor-prep-2.emergent.host"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="portal-yt-cta"
                                className="mt-4 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#DC2626] to-[#991B1B] hover:from-[#991B1B] hover:to-[#DC2626] text-white px-4 py-2.5 rounded-lg font-bold text-sm shadow-[0_6px_20px_rgba(220,38,38,0.35)] transition-all group-hover:scale-[1.02]"
                            >
                                {isAr ? "افتح المكتبة" : "פתח את הספרייה"}
                                <ExternalLink size={14} />
                            </a>
                        </div>
                    </article>

                    {/* ── Window 3: AI Bot ── */}
                    <article
                        data-testid="portal-window-bot"
                        className="group relative overflow-hidden rounded-2xl border border-[#D4761F]/25 bg-white shadow-[0_10px_30px_rgba(212,118,31,0.10)] hover:shadow-[0_18px_40px_rgba(212,118,31,0.30)] hover:-translate-y-1 transition-all duration-300 min-h-[280px] flex flex-col"
                    >
                        <div className="relative h-32 overflow-hidden bg-gradient-to-br from-[#0B1F3D] to-[#1A365D]">
                            <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "linear-gradient(rgba(212,175,55,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.5) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
                            <MessageCircle size={80} className="absolute -end-4 -bottom-4 text-[#F8D89C]/30" />
                            <div className="relative p-5 h-full flex flex-col justify-between">
                                <span className="inline-block bg-[#D4761F]/40 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-full self-start">
                                    {isAr ? "متوفر 24/7" : "זמין 24/7"}
                                </span>
                                <h3 className="font-serif text-xl font-bold text-white">
                                    {isAr ? "المستشار الذكي" : "היועץ החכם"}
                                </h3>
                            </div>
                        </div>
                        <div className="p-5 flex-1 flex flex-col">
                            <p className="text-[#475569] text-sm leading-relaxed flex-1">
                                {isAr
                                    ? "بوت ذكي يجيب على أسئلتك حول المسارات، الرسوم، المنح، وإجراءات التسجيل."
                                    : "בוט חכם עונה על שאלות בנוגע למסלולים, שכר לימוד, מלגות, ותהליך הרישום."}
                            </p>
                            <button
                                onClick={onStartBot}
                                data-testid="portal-bot-cta"
                                className="mt-4 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#0B1F3D] to-[#1A365D] hover:from-[#1A365D] hover:to-[#0B1F3D] text-white px-4 py-2.5 rounded-lg font-bold text-sm shadow-[0_6px_20px_rgba(11,31,61,0.35)] transition-all group-hover:scale-[1.02]"
                            >
                                {isAr ? "ابدأ محادثة" : "התחל שיחה"}
                                {arrow}
                            </button>
                        </div>
                    </article>

                    {/* ── Window 4: Career Match Quiz ── */}
                    <article
                        data-testid="portal-window-quiz"
                        className="group relative overflow-hidden rounded-2xl border border-[#7C3AED]/25 bg-white shadow-[0_10px_30px_rgba(124,58,237,0.10)] hover:shadow-[0_18px_40px_rgba(124,58,237,0.30)] hover:-translate-y-1 transition-all duration-300 min-h-[280px] flex flex-col"
                    >
                        <div className="relative h-32 overflow-hidden bg-gradient-to-br from-[#7C3AED] to-[#5B21B6]">
                            <Sparkles size={80} className="absolute -end-4 -bottom-4 text-white/25" />
                            <div className="relative p-5 h-full flex flex-col justify-between">
                                <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-full self-start">
                                    {isAr ? "20 سؤالًا · 3 دقائق" : "20 שאלות · 3 דקות"}
                                </span>
                                <h3 className="font-serif text-xl font-bold text-white">
                                    {isAr ? "اختبار تحديد المسار" : "מבחן התאמה"}
                                </h3>
                            </div>
                        </div>
                        <div className="p-5 flex-1 flex flex-col">
                            <p className="text-[#475569] text-sm leading-relaxed flex-1">
                                {isAr
                                    ? "غير متأكد من التخصص؟ اختبار قصير يحدد لك أفضل مسار حسب ميولك واهتماماتك."
                                    : "לא בטוח באיזה מסלול לבחור? מבחן קצר יגלה לך את המסלול המתאים ביותר לפי הנטיות שלך."}
                            </p>
                            <button
                                onClick={() => setQuizOpen(true)}
                                data-testid="portal-quiz-cta"
                                className="mt-4 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] hover:from-[#5B21B6] hover:to-[#7C3AED] text-white px-4 py-2.5 rounded-lg font-bold text-sm shadow-[0_6px_20px_rgba(124,58,237,0.35)] transition-all group-hover:scale-[1.02]"
                            >
                                <Sparkles size={14} />
                                {isAr ? "ابدأ الاختبار" : "התחל מבחן"}
                            </button>
                        </div>
                    </article>
                </div>
            </section>

            {/* Quiz modal */}
            {quizOpen && <CareerQuiz lang={lang} onClose={() => setQuizOpen(false)} />}
        </>
    );
}
